import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { euro } from "@/lib/accounting";
import { isoDate } from "@/lib/format";
import { Empty } from "@/components/Common";
import { DatePicker } from "@/components/DatePicker";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const firstOfMonth = () => { const d = new Date(); d.setDate(1); return isoDate(d); };
const satTone = (v) => (v == null ? "" : v >= 85 ? "text-emerald-700" : v >= 60 ? "text-amber-700" : "text-red-700");

const Bar = ({ v }) => (
  <div className="flex items-center gap-2">
    <div className="h-1.5 w-20 bg-muted"><div className={`h-1.5 ${v >= 85 ? "bg-emerald-600" : v >= 60 ? "bg-amber-500" : "bg-red-600"}`} style={{ width: `${Math.min(v || 0, 100)}%` }} /></div>
    <span className={`font-mono text-xs font-semibold ${satTone(v)}`}>{v == null ? "—" : `${v.toLocaleString("it-IT")}%`}</span>
  </div>
);

export const ProductivityTab = () => {
  const { nameOf, companyId } = useData();
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(isoDate(new Date()));
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/control/productivity", { params: { date_from: from, date_to: to, company_id: companyId || undefined } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), [from, to, companyId]);
  useEffect(() => { load(); }, [load]);
  const t = data?.totals;
  return (
    <div data-testid="productivity-tab">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="w-56"><DatePicker compact value={from} onChange={setFrom} testId="productivity-from" /></div>
        <span className="text-sm">→</span>
        <div className="w-56"><DatePicker compact value={to} onChange={setTo} testId="productivity-to" /></div>
      </div>
      {t && <div className="mb-3 grid grid-cols-2 gap-px border bg-border sm:grid-cols-5" data-testid="productivity-totals">
        {[["Interventi chiusi", t.reports], ["Ore lavorate", t.hours.toLocaleString("it-IT")], ["Ricavo prodotto", euro(t.revenue)], ["Ricavo per ora", t.revenue_per_hour == null ? "—" : euro(t.revenue_per_hour)], ["Saturazione", t.saturation_pct == null ? "—" : `${t.saturation_pct.toLocaleString("it-IT")}%`]].map(([l, v]) => (
          <div key={l} className="bg-white p-3"><div className="micro-label">{l}</div><div className="mt-1 font-display text-2xl">{v}</div></div>
        ))}
      </div>}
      {!data?.rows.length ? <Empty text="Nessun tecnico con attività nel periodo" testId="productivity-empty" /> : (
        <div className="overflow-x-auto border bg-white">
          <Table>
            <TableHeader><TableRow className="bg-muted/50"><TableHead>Tecnico</TableHead><TableHead>Società</TableHead><TableHead className="text-right">Interventi</TableHead><TableHead className="text-right">Positivi</TableHead><TableHead className="text-right">Ore lavorate</TableHead><TableHead className="text-right">Ore previste</TableHead><TableHead>Saturazione</TableHead><TableHead className="text-right">Ricavo</TableHead><TableHead className="text-right">€ / ora</TableHead><TableHead className="text-right">Costo</TableHead><TableHead className="text-right">Margine</TableHead></TableRow></TableHeader>
            <TableBody>{data.rows.map((r) => (
              <TableRow key={r.employee_id} data-testid={`productivity-row-${r.employee_id}`}>
                <TableCell className="text-sm font-semibold">{r.name}</TableCell><TableCell className="text-xs">{nameOf("companies", r.company_id, "code")}</TableCell>
                <TableCell className="text-right font-mono text-xs">{r.reports}</TableCell><TableCell className="text-right font-mono text-xs">{r.positive}</TableCell>
                <TableCell className="text-right font-mono text-xs">{r.hours}</TableCell><TableCell className="text-right font-mono text-xs">{r.planned}</TableCell>
                <TableCell><Bar v={r.saturation_pct} /></TableCell>
                <TableCell className="text-right font-mono text-xs">{euro(r.revenue)}</TableCell><TableCell className="text-right font-mono text-xs">{r.revenue_per_hour == null ? "—" : euro(r.revenue_per_hour)}</TableCell>
                <TableCell className="text-right font-mono text-xs">{euro(r.cost)}</TableCell><TableCell className={`text-right font-mono text-xs ${r.margin < 0 ? "text-red-700" : ""}`}>{euro(r.margin)}</TableCell>
              </TableRow>))}</TableBody>
          </Table>
        </div>
      )}
      <p className="mt-2 text-xs text-muted-foreground">Ore previste: orario settimanale del contratto o turni pianificati, escluse le assenze approvate. Ricavo: produzione validata dei rapportini del tecnico.</p>
    </div>
  );
};
